import axios from 'axios';

import { apiClient } from '@/lib/api';

import type { operations, paths } from './schema';

/**
 * The typed edge of the API.
 *
 * Every request the app makes goes through these four helpers, and their request and response
 * types come from `schema.d.ts`, which is generated from the backend's own OpenAPI document
 * (`npm run api:types`). Nothing here declares a response shape by hand — that is the rule in
 * `.claude/rules/frontend.md`, and `api.test.ts` enforces it: a hand-written response interface,
 * or a URL the schema does not describe, fails the suite rather than the browser.
 */

type Paths = paths;

type PathsWith<Method extends string> = {
  [P in keyof Paths]: Paths[P] extends Record<Method, unknown> ? P : never;
}[keyof Paths];

export type GetPath = PathsWith<'get'>;
export type PostPath = PathsWith<'post'>;
export type PatchPath = PathsWith<'patch'>;
export type DeletePath = PathsWith<'delete'>;

type JsonOf<Responses> = Responses extends {
  200: { content: { 'application/json': infer Body } };
}
  ? Body
  : Responses extends { 201: { content: { 'application/json': infer Created } } }
    ? Created
    : Responses extends { 204: unknown }
      ? void
      : never;

type Operation<P extends keyof Paths, M extends string> =
  Paths[P] extends Record<M, infer Op> ? Op : never;

export type ResponseOf<P extends keyof Paths, M extends string> =
  Operation<P, M> extends { responses: infer R } ? JsonOf<R> : never;

export type BodyOf<P extends keyof Paths, M extends string> =
  Operation<P, M> extends { requestBody?: { content: { 'application/json': infer B } } }
    ? B
    : never;

export type QueryOf<P extends keyof Paths, M extends string> =
  Operation<P, M> extends { parameters: { query?: infer Q } } ? Q : never;

/** The statuses the app reads as an answer. Everything else the document declares is a refusal. */
type SucceededStatus = 200 | 201 | 202 | 204;

type JsonBody<Response> = Response extends { content: { 'application/json': infer Body } }
  ? Body
  : never;

/** The JSON bodies one operation declares for a status outside `SucceededStatus`. */
type FailureBodyOf<Op> = Op extends { responses: infer Responses }
  ? JsonBody<Responses[Exclude<keyof Responses, SucceededStatus> & keyof Responses]>
  : never;

/**
 * **Every refusal body the document declares, as one union** — derived from `operations`, so it is
 * the backend's own set of refusals and not a list maintained here.
 *
 * Today that is `DuplicateWarningOut` — the 409 of `POST /api/v1/people/` **and** of
 * `PATCH /api/v1/people/{person_id}/`, which has answered the same body since backend TASK-109
 * (ADR 0014) — `ActiveConsiderationOut` / `ConsiderationRefused` (the 409 of opening a
 * consideration and of the three status actions), `ContrastProblem` / `LogoProblem` (the two 422s
 * of the branding surface), `ScoresRefused` / `SubmissionRefused` (the two 422s of the interview
 * surface) and the plain `ErrorResponse` envelope every handled `HttpError` uses. A body the
 * document does **not** declare — a 401, a 404, prod's HTML 500 — still arrives at runtime; it is
 * read through `bodyWith`, which checks the field is there before believing any of this.
 *
 * ⚠️ **This paragraph is a list of the document's contents and it has gone stale twice, so it now
 * has a gate rather than a habit** (BUG-039). Sprint 13's own diff added the two branding bodies
 * and left the sentence naming six; the duplicate 409 went on being described as one route a
 * release after TASK-109 gave it two. `api.test.ts` derives the set from `openapi.snapshot.json`
 * and reads this docblock back **in both directions** — a declared body it does not name fails, a
 * schema it names that the document no longer declares as a refusal fails — and asserts both routes
 * that answer the duplicate 409 are named here. The prose is kept because it says *why* each body
 * exists, which no derivation can; what it may no longer do is drift.
 */
export type ApiErrorBody = FailureBodyOf<operations[keyof operations]>;

/** The keys of every member of a union, rather than the ones they happen to share. */
type KeysOfUnion<T> = T extends unknown ? keyof T : never;

/** What `ApiError.bodyWith` may be asked for: a field some declared refusal body carries. */
type ApiErrorBodyField = KeysOfUnion<ApiErrorBody>;

/**
 * The routes where a caller's reach is narrower than their tenant, so a `404` there means
 * **"outside what you may see"** and not "no such thing" (backend TASK-029, 2026-07-24). An
 * `Interviewer` naming a candidate they were not assigned to receives byte-for-byte what an id that
 * never existed receives, because a `403` would confirm that the candidate exists to somebody with
 * no business knowing.
 *
 * ⚠️ **It matches a record, not a collection.** The rule needs somebody to have *named a record*:
 * `/api/v1/people/42/` is a candidate this caller may not reach, but `/api/v1/people/` is the list
 * every caller may ask for — filtered to their reach, never refused — so a 404 there is a broken
 * deployment and must read like one, the same argument the dashboard already makes. A prefix match
 * could not tell the two apart and rendered *"You do not have access to this candidate."* for a
 * missing route. The id segment is what makes it decidable.
 *
 * The surface is people, their considerations and the interview runs on them. A route joins it when
 * the API narrows it per caller, not when it can merely 404.
 */
const CANDIDATE_SCOPED_RECORD = /^\/api\/v1\/(people|considerations|interviews)\/\d+(\/|$)/;

/** Whether a 404 from this path is a reach answer rather than a missing record. */
export function isCandidateScopedPath(path: string | undefined): boolean {
  if (!path) return false;
  return CANDIDATE_SCOPED_RECORD.test(path);
}

/**
 * What the app knows about a failed request. The status is kept because the screens branch on it:
 * 401 ends the session (TASK-056), 403 is a refused action, 404 on a candidate route renders the
 * Forbidden state (backend TASK-029), 422 is a validation answer and 429 is a throttle.
 *
 * The **path** is kept for the same reason: 404 alone cannot say whether the record is missing or
 * merely out of reach, and only the route it came from can. Deciding that here, once, is what keeps
 * a future candidate screen from having to remember the rule.
 *
 * ⚠️ **The body is kept because some refusals are the interaction, not the failure** (TASK-064).
 * Until now this class dropped it, and three tasks in a row recorded that as a debt: the 409 of
 * `POST /people/` carries `possible_duplicates` — the whole of ADR 0014 and of `DESIGN.md` §6.4 —
 * and the 409 of `POST /people/{id}/considerations/` carries `existing_consideration`, which is the
 * row a screen has to link to instead of guessing which run is in the way. Discarding those two left
 * a screen with a status code where the design asks for a sentence naming a person.
 *
 * It is **one** mechanism for every such body, on purpose: read through `bodyWith`, whose whole
 * argument is a field the document declares, so the next route that answers with something to render
 * needs no second error class and no second code path.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail?: string;
  /** The path that was requested, as the code asked for it (parameters already substituted). */
  readonly path?: string;
  /**
   * The parsed response body, as the document types it (`ApiErrorBody`).
   *
   * ⚠️ **A server can send anything, so nothing may be read off this directly** — that is what
   * `bodyWith` is for: it checks the field is present before handing back a body typed as the member
   * that carries it. The field declared here is what the *document* promises, not what arrived.
   */
  readonly body?: ApiErrorBody;

  constructor(status: number, detail?: string, path?: string, body?: ApiErrorBody) {
    super(detail ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.path = path;
    this.body = body;
  }

  /**
   * The refusal body, **if it carries `field`** — narrowed to the declared shapes that have it.
   *
   * ```ts
   * error.bodyWith('possible_duplicates')?.possible_duplicates // DuplicateWarningOut (ADR 0014)
   * error.bodyWith('existing_consideration')?.existing_consideration // ActiveConsiderationOut
   * ```
   *
   * The **field** is the discriminator, not the status code, because the API says so: opening a
   * consideration answers 409 both for an active run at that vacancy and for an anonymised person
   * (ADR 0009), and `ConsiderationRefused`'s own docstring records that *"the two 409s of this route
   * are told apart by their shape, not by a null field"*. A caller that finds neither field has a
   * refusal it has no words for, and must say something a human understands rather than the code.
   */
  bodyWith<Field extends ApiErrorBodyField>(
    field: Field,
  ): Extract<ApiErrorBody, Record<Field, unknown>> | undefined {
    const body = this.body;
    if (body === undefined || body === null || typeof body !== 'object') return undefined;
    if (!(field in body)) return undefined;

    // The one assertion in the mechanism, and it is the point of having only one: `field in body`
    // has just established at runtime what the document types statically.
    return body as Extract<ApiErrorBody, Record<Field, unknown>>;
  }

  /**
   * The backend throttles login at 5/min per IP (BUG-005) and every authenticated route at 300/min
   * per user (BUG-011). A throttled request is not a broken one and must not be reported as
   * "something went wrong" — the screens ask this and say so.
   */
  get isThrottled(): boolean {
    return this.status === 429;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Another tenant's id, an unassigned candidate, and an id that never existed are all 404. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * No answer at all — the request never reached a server that could give it a status. §8's offline
   * state, and never "wrong password": reporting a lost connection as a refused credential sends
   * the user to reset something that was never the problem.
   */
  get isOffline(): boolean {
    return this.status === 0;
  }

  /**
   * **The server was not there to answer: 502, 503 or 504.**
   *
   * These three are composed by whatever sits between the browser and Django — nginx here — when the
   * upstream is stopped, restarting, overloaded or too slow. The application never saw the request,
   * so the response says **nothing whatever about the reader**: a stopped `web` container answers a
   * 502 to a perfectly valid session cookie, which is exactly how this was measured (BUG-103, with
   * the container back the same session resumed with no re-authentication).
   *
   * ⚠️ **Which is why it must never be handled as a 401.** Only a 401 means the session is gone;
   * this means *try again*. Sending the reader to sign-in for one tells them the single thing that
   * is not true, and the sign-in they then attempt meets the same 502 — so they learn nothing and
   * go looking for a session problem that does not exist while the outage goes unreported.
   *
   * ⚠️ **And nothing may be discarded on it.** `readerChanged` is anchored on `isUnauthenticated`
   * and stays there: a door that threw away a live session is what BUG-045 and BUG-066 cost, and an
   * unreachable server is the one case where the client's state is still exactly right.
   *
   * A 500 is deliberately **not** here: the application answered, so it is an ordinary §8 Error.
   */
  get isUnreachable(): boolean {
    return this.status === 502 || this.status === 503 || this.status === 504;
  }

  /**
   * A 404 that means "not yours to see" rather than "not there" — see `CANDIDATE_SCOPED_RECORD`.
   * `DESIGN.md` §8 renders it as the **Forbidden** state, which is what answers the objection that
   * a 404 reads badly for a mistyped URL: both cases say the same sentence, and neither discloses.
   */
  get isOutsideReach(): boolean {
    return this.isNotFound && isCandidateScopedPath(this.path);
  }
}

/**
 * The envelope's `detail`, **and only when it is a sentence.**
 *
 * django-ninja's 422 answers `detail` as a *list* of per-field problems (`SubmissionRefused`), so the
 * one field every refusal appears to share is not one type. Passing the list to `Error` would render
 * `[object Object]` wherever `message` or `detail` is shown; a caller that wants the list asks
 * `bodyWith('detail')` for it.
 */
function detailOf(body: ApiErrorBody | undefined): string | undefined {
  if (body === undefined || body === null || typeof body !== 'object') return undefined;
  if (!('detail' in body)) return undefined;
  return typeof body.detail === 'string' ? body.detail : undefined;
}

function toApiError(error: unknown): ApiError {
  // `axios.isAxiosError` reads the flag axios sets rather than the prototype: an `instanceof`
  // check silently fails whenever the error crosses a module boundary — and then a 429 or a 401
  // arrives here as status 0, which is exactly the wrong answer for both.
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const body = error.response?.data as ApiErrorBody | undefined;
    return new ApiError(status, detailOf(body), error.config?.url, body);
  }
  return new ApiError(0, error instanceof Error ? error.message : undefined);
}

/** Substitutes `{param}` segments; the schema's path is the literal key, so this stays typed. */
export function fillPath(path: string, params?: Record<string, string | number>): string {
  if (!params) return path;
  return Object.entries(params).reduce(
    (filled, [key, value]) => filled.replace(`{${key}}`, String(value)),
    path,
  );
}

/**
 * What a query parameter may be. The **array** is the reason this type exists: `?status=` on
 * `GET /api/v1/considerations/` is repeatable (`DESIGN.md` §6.3's status filter), and until
 * TASK-061 this type did not admit one at all.
 */
export type QueryValue =
  | string
  | number
  | boolean
  | readonly (string | number)[]
  | null
  | undefined;

interface RequestOptions {
  params?: Record<string, string | number>;
  query?: Record<string, QueryValue>;
}

/**
 * ⚠️ **Repeated parameters, never `status[]` — and this is the difference between a filtered list
 * and one that only looks filtered.**
 *
 * axios's own serialiser brackets arrays: `{ status: ['NEW', 'OFFER'] }` goes out as
 * `status%5B%5D=NEW&status%5B%5D=OFFER`. django-ninja binds a `list[...]` query parameter from
 * **repeated** keys and ignores that spelling entirely, so the filter silently disappears and the
 * API answers with *every* row — `HIRED` and `REJECTED` included — while the screen's chip claims a
 * filter is applied. That is §6.3's own named failure (*"a filtered list that looks unfiltered is
 * how people conclude a candidate was lost"*) arriving through a query-string detail, and no status
 * code and no test on the params object would show it. `candidates.test.tsx` asserts the produced
 * URL, and pins axios's default alongside it so the reason cannot be forgotten.
 *
 * `undefined` and `null` are dropped (a filter that is not applied sends nothing), and so is an
 * **empty array**: `?status=` with no value is a value to Pydantic, not an absent parameter.
 */
export function serializeQuery(query: Record<string, QueryValue>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    const values: readonly (string | number | boolean)[] = Array.isArray(value) ? value : [value];
    for (const item of values) search.append(key, String(item));
  }

  return search.toString();
}

const paramsSerializer = { serialize: serializeQuery };

export async function apiGet<P extends GetPath>(
  path: P,
  options: RequestOptions = {},
): Promise<ResponseOf<P, 'get'>> {
  try {
    const response = await apiClient.get(fillPath(path as string, options.params), {
      params: options.query,
      paramsSerializer,
    });
    return response.data as ResponseOf<P, 'get'>;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function apiPost<P extends PostPath>(
  path: P,
  body?: BodyOf<P, 'post'>,
  options: RequestOptions = {},
): Promise<ResponseOf<P, 'post'>> {
  try {
    const response = await apiClient.post(fillPath(path as string, options.params), body, {
      params: options.query,
      paramsSerializer,
    });
    return response.data as ResponseOf<P, 'post'>;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function apiPatch<P extends PatchPath>(
  path: P,
  body: BodyOf<P, 'patch'>,
  options: RequestOptions = {},
): Promise<ResponseOf<P, 'patch'>> {
  try {
    const response = await apiClient.patch(fillPath(path as string, options.params), body, {
      params: options.query,
      paramsSerializer,
    });
    return response.data as ResponseOf<P, 'patch'>;
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * `DELETE`, and there is exactly **one** such route in this API: `/api/v1/branding/logo/`
 * (backend TASK-098). Nothing in this product deletes feedback — `POST …/anonymize/` clears text and
 * `POST …/reject/` closes a run — and the backend pins that with a sweep over its own OpenAPI
 * document (`tests/test_submitted_at_is_not_writable.py`): a `delete` anywhere on `/interviews`,
 * `/considerations` or `/people` fails, because deleting the row would be the cleanest un-submit of
 * all. So this helper is deliberately narrow rather than general-purpose.
 */
export async function apiDelete<P extends DeletePath>(
  path: P,
  options: RequestOptions = {},
): Promise<ResponseOf<P, 'delete'>> {
  try {
    const response = await apiClient.delete(fillPath(path as string, options.params), {
      params: options.query,
      paramsSerializer,
    });
    return response.data as ResponseOf<P, 'delete'>;
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * The one multipart request in the product: a tenant logo (backend TASK-098).
 *
 * ⚠️ **`Content-Type` is left to the browser on purpose.** A hand-set `multipart/form-data` header
 * carries no boundary, and the request then fails at parsing rather than at validation — a confusing
 * 422 about a field that was sent. The browser composes the header from the `FormData` body.
 *
 * ⚠️ **The file is validated by the server from the bytes, not by this call.** A client check on the
 * extension or on `File.type` is a courtesy (§6.10 states the bounds before a file is chosen) and
 * never the gate: the server **walks the container** — every PNG chunk with its CRC-32 verified,
 * every JPEG marker segment with its length, a scan required, and in both cases the file must end
 * **exactly** at `IEND` / `EOI` — and it refuses an SVG **by name**, because an SVG is a document
 * that can carry script and would be served from this origin. ⚠️ *"It sniffs magic bytes"* is
 * **retracted** (BUG-061, 2026-08-09): the signature chooses which walk to run and decides nothing,
 * because while it *was* the whole check an eight-byte prefix with an HTML document behind it was
 * stored and served back verbatim. It still does not **decode** the image, so a well-formed
 * container carrying bytes no decoder would open is accepted.
 *
 * ⚠️ **Which adds a refusal a caller has to be able to explain: anything after the end of the image
 * is a 422.** A perfectly viewable PNG or JPEG with padding or an EXIF trailer appended — files some
 * tools emit — is now refused, and that is the whole of the polyglot defence rather than an
 * oversight: an exemption for "harmless" trailing bytes is an exemption for the attack. The server
 * answers it with a **separate sentence** from *"not a PNG or a JPEG"* (*"…or something is appended
 * after the end of the image. Open it in an image editor and export it again"*), because the
 * reader's next step differs — so a screen renders the 422's own `detail` and never a message of its
 * own, which would send somebody looking at a good PNG off to export another PNG.
 */
export async function apiUpload<P extends PostPath>(
  path: P,
  file: File,
  options: RequestOptions = {},
): Promise<ResponseOf<P, 'post'>> {
  const body = new FormData();
  body.append('file', file);
  try {
    const response = await apiClient.post(fillPath(path as string, options.params), body);
    return response.data as ResponseOf<P, 'post'>;
  } catch (error) {
    throw toApiError(error);
  }
}
